/* What to remove when a workspace is over its plan — STORY-045 part 3.
 *
 * ── per meter, never global ──────────────────────────────────────────────────
 * One line per meter that is over, and nothing at all when none is. Freezing a
 * whole workspace over one meter punishes work that has nothing to do with the
 * overage, and the story is explicit that the lock is per meter.
 *
 * ── it says a number, and the number comes from the server ───────────────────
 * `overBy` is computed once — in `checkLimit` and in the usage endpoint — and
 * travels on the wire. This component does no arithmetic. A client working out
 * `used - limit` itself would disagree with the refusal the moment the two were
 * written differently, and telling somebody to remove 4 while the server still
 * refuses at 5 is worse than saying nothing at all.
 *
 * ── full is not over ─────────────────────────────────────────────────────────
 * A workspace AT its ceiling reports `overBy: 0` and gets no banner. It is not
 * locked out of what it already has — it simply cannot add — and a warning
 * appearing the moment somebody uses their last contact would be noise on the
 * most ordinary state in the product.
 */

import type { UsageResponse } from '@eventuley/contracts';
import { useEffect, useSyncExternalStore } from 'react';
import {
  clearRefusal,
  refusalSnapshot,
  subscribeToRefusals,
} from '../../../shared/api/refusals.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { Callout } from '../../../shared/ui/index.js';

/** The meters, in the order the banner lists them. */
const METERS = ['activeEvents', 'seats', 'contacts'] as const;

type Meter = (typeof METERS)[number];

/* An explicit map rather than a constructed key. `i18n.md` forbids
 * `t('over_limit.' + meter)` because it defeats extraction and unused-key
 * detection. */
const METER_NAME = {
  activeEvents: 'over_limit.events',
  seats: 'over_limit.seats',
  contacts: 'over_limit.contacts',
} as const satisfies Record<Meter, string>;

export interface OverLimitBannerProps {
  readonly usage: UsageResponse | undefined;
}

export function OverLimitBanner({ usage }: OverLimitBannerProps) {
  const t = useTranslate();

  /* Whether a write was just refused — STORY-045.
   *
   * The lock itself always worked; what was missing was anybody being told. A
   * refused save showed nothing at all on most screens, which from a chair is
   * indistinguishable from the feature not existing. Reported exactly that way
   * on 2026-09-05, against a workspace that was refusing every write. */
  const refusedAt = useSyncExternalStore(subscribeToRefusals, refusalSnapshot, () => null);

  const over = usage ? METERS.filter((meter) => usage.overBy[meter] > 0) : [];

  /* Clears itself once the workspace fits, so the line cannot outlive the
   * lock that caused it. In an effect because it is a write to a store during
   * what would otherwise be render. */
  useEffect(() => {
    if (over.length === 0 && refusedAt !== null) clearRefusal();
  }, [over.length, refusedAt]);

  /* Nothing while it loads. A banner that flashes "you are over your limit"
   * before the numbers arrive would be a lie shown to every workspace. */
  if (!usage) return null;
  if (over.length === 0) return null;

  return (
    <Callout tone="danger" title={t('over_limit.title')}>
      <ul className="over-limit__list">
        {over.map((meter) => (
          <li key={meter}>
            {/* One key, one whole sentence, with the count as an ICU argument.
                `i18n.md` forbids assembling this from fragments: Turkish
                attaches its suffixes to the word, so a sentence built in
                English word order cannot be reassembled. */}
            {t('over_limit.meter', {
              count: usage.overBy[meter],
              meter: t(METER_NAME[meter]),
              used: usage[meter],
              limit: usage.limits[meter] ?? 0,
            })}
          </li>
        ))}
      </ul>
      {/* Said only when something has actually been refused, so it is news
          rather than a permanent scold. */}
      {refusedAt === null ? null : <p className="over-limit__refused">{t('over_limit.refused')}</p>}
      {/* Both exits, always. Removing things is one way out and paying is the
          other, and a banner offering only the first reads as a threat. */}
      <p className="over-limit__exits">{t('over_limit.exits')}</p>
    </Callout>
  );
}
